import { useCallback, useEffect, useRef, useState } from "react";

export const useScrollBottomHandler = ({
  handler
}: {
  handler: () => void;
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    const eventHandler = (event: Event) => {
      if (!event.target) return;
      const { scrollHeight, scrollTop, clientHeight } =
        event.target as HTMLDivElement;

      if (scrollHeight === clientHeight) {
        return;
      }

      if (Math.abs(scrollHeight - clientHeight - scrollTop) === 0) {
        handler();
      }
    };
    element?.addEventListener("scroll", eventHandler);

    return () => {
      element?.removeEventListener("scroll", eventHandler);
    };
  }, [handler]);

  return elementRef;
};

export const addScrollOnTopListener = (
  element: HTMLDivElement,
  handler: () => void,
  threshold: number = 2
) => {
  const eventHandler = (event: Event) => {
    if (!event.target) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } =
      event.target as HTMLDivElement;

    if (scrollHeight === clientHeight) {
      return;
    }

    if (scrollTop <= threshold) {
      handler();
    }
  };

  if (element) {
    element.addEventListener("scroll", eventHandler);
  }

  return () => {
    element.removeEventListener("scroll", eventHandler);
  };
};

export const scrollElementTo = (element: HTMLElement, y: number) => {
  if (typeof element.scrollTo === "function") {
    element.scrollTo(0, y);
  } else {
    // EDGE fix
    element.scrollTop = y;
  }
};

export const useUpdateAppHeightCssVariable = () => {
  useEffect(() => {
    const updateAppHeight = () => {
      document.documentElement.style.setProperty(
        "--app-height",
        `${window.innerHeight}px`
      );
    };

    window.addEventListener("resize", updateAppHeight);
    updateAppHeight();
    return () => {
      window.removeEventListener("resize", updateAppHeight);
    };
  }, []);
};

export const scrollToFormWithError = (element: HTMLDivElement) => {
  setTimeout(() => {
    element?.querySelector(".ant-form-item-has-error")?.scrollIntoView();
  }, 100);
};

const TOP_SCROLL_THRESHOLD = 46; // randomly chosen px value

export const useSwitchIsDivShownOnScroll = (
  scrollThreshold: number | undefined = TOP_SCROLL_THRESHOLD
) => {
  const [isDivShownOnScroll, setIsDivShownOnScroll] = useState(true);

  const scrollHandler = useCallback((event: Event) => {
    if (!event.target) return;

    const target = event.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    if (scrollHeight === clientHeight) return;

    const previousScrollTopStr = target.dataset.previousScrollTop;

    if (previousScrollTopStr === undefined) {
      target.dataset.previousScrollTop = scrollTop.toString();
      return;
    }

    const previousScrollTop = Number(previousScrollTopStr);
    if (isNaN(previousScrollTop)) {
      target.dataset.previousScrollTop = scrollTop.toString();
      return;
    }

    const isScrollingUp = scrollTop < previousScrollTop;
    target.dataset.previousScrollTop = scrollTop.toString();

    if (isScrollingUp) {
      setIsDivShownOnScroll(true);
    } else if (scrollTop > scrollThreshold) {
      setIsDivShownOnScroll(false);
    }
  }, []);

  return { isDivShownOnScroll, setIsDivShownOnScroll, scrollHandler };
};
